import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ROTATION_INTERVAL_MS = 3500;
const REDUCED_MOTION_DURATION = 0.001;

export interface RotatingHeroTextProps {
  /** Lines to rotate through (one visible at a time). */
  lines: string[];
  /** Rotation interval in ms. Default 3500. */
  intervalMs?: number;
  /** Optional className for the wrapper (fixed-height container). */
  className?: string;
  /** Optional className for the visible line. */
  lineClassName?: string;
  /** Tag for the rotating line: 'p', 'div', or 'h1'. Default 'p'. */
  as?: 'p' | 'div' | 'h1';
}

/**
 * Synthesia-style rotating subheadline. Fade + upward motion, pause on hover.
 * Respects prefers-reduced-motion. Fixed-height container to avoid layout shift.
 */
export function RotatingHeroText({
  lines,
  intervalMs = ROTATION_INTERVAL_MS,
  className = '',
  lineClassName = '',
  as: Tag = 'p',
}: RotatingHeroTextProps) {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const listener = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  const next = useCallback(() => {
    if (lines.length <= 1) return;
    setIndex((i) => (i + 1) % lines.length);
  }, [lines.length]);

  useEffect(() => {
    if (isPaused || lines.length <= 1) return;
    const id = window.setInterval(next, intervalMs);
    return () => window.clearInterval(id);
  }, [isPaused, next, intervalMs, lines.length]);

  if (!lines.length) return null;

  const duration = prefersReducedMotion ? REDUCED_MOTION_DURATION : 0.35;

  return (
    <div
      className={`min-h-[4rem] sm:min-h-[3.5rem] ${className}`.trim()}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={index}
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -8 }}
          transition={{
            duration,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          className="text-center w-full"
        >
          <Tag className={lineClassName}>{lines[index]}</Tag>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
